import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  Order,
  OrderHistory,
  OrderItem,
} from "../../services/types/order.type";

export const ordersApi = createApi({
  reducerPath: "ordersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_ADMIN_API_URL, // например, "https://api.miyukon.store/admin"
    credentials: "include",
  }),
  endpoints: (builder) => ({
    // Заказы
    getOrders: builder.mutation<Order[], string | undefined>({
      query: (status) => `/orders${status ? "?status=" + status : ""}`,
      transformResponse: (response: { status: string; data: Order[] }) =>
        response.data,
    }),
    getOrderById: builder.query<Order, string>({
      query: (uuid) => `/orders/${uuid}`,
      transformResponse: (response: { status: string; data: Order }) =>
        response.data,
    }),
    updateOrder: builder.mutation<Order, Partial<Order> & { uuid: string }>({
      query: ({ uuid, ...changes }) => ({
        url: `/orders/${uuid}`,
        method: "PATCH",
        body: changes,
      }),
      transformResponse: (response: { status: string; data: Order }) =>
        response.data,
    }),
    updateOrderStatus: builder.mutation<
      Order,
      { uuid: string; newStatus: string }
    >({
      query: ({ uuid, newStatus }) => ({
        url: `/orders/${uuid}/status`,
        method: "PATCH",
        body: { newStatus },
      }),
      transformResponse: (response: { status: string; data: Order }) =>
        response.data,
    }),
    deleteOrder: builder.mutation<{ uuid: string }, string>({
      query: (uuid) => ({
        url: `/orders/${uuid}`,
        method: "DELETE",
      }),
      transformResponse: (_: any, __: any, arg: string) => ({ uuid: arg }),
    }),

    // Позиции заказов (Order Items)
    createOrderItem: builder.mutation<
      OrderItem,
      {
        orderUuid: string;
        productUuid: string;
        variantUuid: string;
        quantity: number;
      }
    >({
      query: ({ orderUuid, productUuid, variantUuid, quantity }) => ({
        url: `/orders/${orderUuid}/items`,
        method: "POST",
        body: { productUuid, variantUuid, quantity },
      }),
      transformResponse: (response: { status: string; data: OrderItem }) =>
        response.data,
    }),
    updateOrderItem: builder.mutation<
      OrderItem,
      { orderUuid: string; orderItemUuid: string; changes: Partial<OrderItem> }
    >({
      query: ({ orderUuid, orderItemUuid, changes }) => ({
        url: `/orders/${orderUuid}/items/${orderItemUuid}`,
        method: "PATCH",
        body: changes,
      }),
      transformResponse: (response: { status: string; data: OrderItem }) =>
        response.data,
    }),
    deleteOrderItem: builder.mutation<
      { orderItemUuid: string },
      { orderUuid: string; orderItemUuid: string }
    >({
      query: ({ orderUuid, orderItemUuid }) => ({
        url: `/orders/${orderUuid}/items/${orderItemUuid}`,
        method: "DELETE",
      }),
      transformResponse: (_: any, __: any, arg: { orderItemUuid: string }) => ({
        orderItemUuid: arg.orderItemUuid,
      }),
    }),

    // История заказов (Order History)
    createOrderHistory: builder.mutation<
      OrderHistory,
      { orderUuid: string; previousStatus: string; newStatus: string }
    >({
      query: ({ orderUuid, previousStatus, newStatus }) => ({
        url: `/orders/${orderUuid}/history`,
        method: "POST",
        body: { previousStatus, newStatus },
      }),
      transformResponse: (response: { status: string; data: OrderHistory }) =>
        response.data,
    }),
    getOrderHistory: builder.query<OrderHistory[], { orderUuid: string }>({
      query: ({ orderUuid }) => `/orders/${orderUuid}/history`,
      transformResponse: (response: { status: string; data: OrderHistory[] }) =>
        response.data,
    }),
  }),
});

export const {
  useGetOrdersMutation,
  useGetOrderByIdQuery,
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation,
  useDeleteOrderMutation,
  useCreateOrderItemMutation,
  useUpdateOrderItemMutation,
  useDeleteOrderItemMutation,
  useCreateOrderHistoryMutation,
  useGetOrderHistoryQuery,
} = ordersApi;
